// ============================================================================
// 会话空态的「身份」闸门 —— 头像 / 名称 / 欢迎语只许画一次
//
// 【事故现场】用户原话："chat 默认不是会显示什么用户名+头像+欢迎吗?这玩意竟然
// 会变化三次。第一次是默认的,第二次是在线客服,第三次是一个设置的 customer
// service"。
//
// 【根因】空态卡的每个字段都是一条多级兜底链，而喂它的两个数据源**分两步到达**：
//   · props.conf.headerAgents ← widget 的 /usage/start（客服名/头像）
//   · state.appConfig         ← chat 自己的 /v1/chat（service_title/welcome_message）
// 每一级数据到达时兜底链解析出不同的值，setState 触发重绘，于是级联变脸。
// 修法不是"少几次重绘"（那只是把三次压成两次），而是**在权威答案到达之前不画**
// —— 与品牌色 colorKnown 单闸不变式（theme-color.js resolveColorKnown）同一套
// 思路：先画一个猜的再改，比晚几百毫秒出现难看得多。
//
// 【不变式】
//   1. 身份未定之前，空态卡一个像素都不画（画中性骨架 renderChatSkeleton）。
//   2. 身份一旦定下就**冻结**：之后 headerAgents / appConfig 再怎么到、怎么变，
//      标题/头像/欢迎语都不再改。所以"定"的时机必须是权威答案，不能是半程快照。
//   3. 单调：settled 只从 false 翻 true，永不回退。
//
// 【三端一致：这道闸原生两端早就有，web 是最后一个补上的】
// Android sdk/android/.../list/NewChatLogic.kt 与 iOS
// .../LaneChatUICore/Facade/NewChatLogic.swift 的 chatEmptyStateVisible 就是
//   messagesEmpty && !initialHistoryPending && configReady
// 移植时它们主动比 web 多加了 configReady 这一闸，理由与本文件一字不差。注意
// 两端调用点喂给 configReady 的**不是**外壳那个会被 4s 超时翻真的 flag，而是
// "卡片对象存不存在"（Kotlin `card != null` / Swift `if let card = emptyState`），
// 而卡片只在**真 appConfig** 到手时才组装。也就是说原生的既定取舍是：
//   配置永远不来 → 什么都不画（列表保持空白），而不是超时后拿兜底文案顶上。
// 本文件与之对齐：`identityKnown` 只有一个合法来源 —— 服务端就身份明确说过话。
//
// 【"配置永不到达"为什么终态是空白，而不是兜底卡、也不是一直转圈】
//   · 兜底卡 = 又在猜。它与真配置到达后的卡不同值，回到"变脸"原点，只是把变脸
//     窗口从 300ms 拉长到 4s —— 更慢、更显眼。
//   · 一直转圈同样不可接受，所以骨架**不是**终态：初始化明确失败/服务端没给身份
//     时闸门照样"定"下来，只是定成"没有卡"，骨架随之消失，这一格干干净净地空着。
//     静态、此后不再变，这就是终态；而且 ApiService 30s 内必 resolve/reject
//     （services/api.js 的 AbortController），"永远悬着"不是一个真实状态。
//   · 这一格空着的代价在 web 上还更小：widget 外壳自己有 configReady/colorKnown
//     闸和 renderInitError 错误页（widget.js），网络真断时用户看到的是错误页，
//     根本走不到这里。真正会命中的是 chat.html / 原生 WebView / embed 这些没有
//     外壳的宿主，而那里空白同样是三端一致的行为。
// ============================================================================

/** 空串/纯空白/null 一律当"没值"，与老兜底链的 `||` 语义一致。 */
function firstNonEmpty(...values) {
    for (const v of values) {
        if (typeof v === 'string') {
            if (v.trim() !== '') return v;
        } else if (v !== undefined && v !== null && v !== false) {
            return v;
        }
    }
    return null;
}

/**
 * 服务端"就身份说过话"了吗 —— 闸门唯一的合法来源。
 *
 * /v1/chat 的 appConfig 必然同时带 service_title 与 welcome_message 两个键
 * （ApiController::init 两者都有服务端兜底值，不会缺键）；widget 转发的
 * APPLY_APP_CONFIG 则可能带 undefined 值（widget.js 组 payload 时字段可能没有）。
 * 所以判据是"键有确定值"，不是"键存在"——否则一个全 undefined 的转发帧就能
 * 把闸门骗开，画出一张纯兜底的卡，等真配置到了又变一次，回到事故原点。
 *
 * @param {object} config applyAppConfig 收到的那一份（不是合并后的）
 */
export function isIdentityAnswer(config) {
    if (!config || typeof config !== 'object') return false;
    return config.service_title !== undefined || config.welcome_message !== undefined;
}

/**
 * 空态卡该不该出现 —— 与原生两端的 chatEmptyStateVisible 同名同参同义
 * （messagesEmpty && !initialHistoryPending && configReady）。
 *
 * web 侧 identityKnown 对应原生的 configReady：都是"真 appConfig 到手了吗"，
 * 都不接受超时放行。三端同一个判定写三遍，至少让它们逐字对得上。
 */
export function chatEmptyStateVisible({ messagesEmpty, initialHistoryPending, identityKnown }) {
    return !!messagesEmpty && !initialHistoryPending && !!identityKnown;
}

/**
 * 解析空态卡的身份三件套。**只在闸门打开的那一刻调用一次**，结果冻结进 state。
 *
 * 兜底链与老实现逐字一致（这次改的是"什么时候算数"，不是"算什么"）：
 *   title   = appConfig.service_title || firstAgent.name  || t('brand.service_center')
 *   avatar  = firstAgent.avatar_url   || appConfig.agent_avatar || null
 *   welcome = appConfig.welcome_message || conf.introMessage || t('brand.default_welcome')
 *
 * 注：老实现的 welcome 首选是 state.welcomeMessage，而它唯一的写入点就是
 * applyAppConfig 里的 config.welcome_message，与 appConfig.welcome_message 同源
 * 同值，这里合并成一条，少一个会漂移的副本。
 *
 * 品牌色（卡片无头像时的圆底）**不**进这份快照：它归 colorKnown 闸门管，且用户
 * 手动切亮暗色时本来就该跟着变，冻结它反而会把暗色下的卡片钉死在亮色主色上。
 *
 * i18n 兜底由调用方喂进来（defaultTitle/defaultWelcome），本模块不 import '../i18n'
 * —— 与原生 chatEmptyStateOf(defaultTitle:, defaultWelcome:) 同一签名，也让这层
 * 能被 node --test 直接跑（i18n 是目录导入，node ESM 解析不了）。
 *
 * @param {object} input.appConfig 合并后的 appConfig（Chat.appConfig 实例字段）
 * @param {object} input.conf      props.conf（widget theme：headerAgents / introMessage）
 * @param {string} input.defaultTitle   t('brand.service_center')
 * @param {string} input.defaultWelcome t('brand.default_welcome')
 * @returns {{title: string, avatarUrl: (string|null), welcome: string}}
 */
export function resolveEmptyStateIdentity({ appConfig, conf, defaultTitle, defaultWelcome } = {}) {
    const cfg = appConfig && typeof appConfig === 'object' ? appConfig : {};
    const c = conf && typeof conf === 'object' ? conf : {};
    const agents = Array.isArray(c.headerAgents) ? c.headerAgents : [];
    const firstAgent = (agents[0] && typeof agents[0] === 'object') ? agents[0] : {};

    return {
        title: firstNonEmpty(cfg.service_title, firstAgent.name, defaultTitle) || '',
        avatarUrl: firstNonEmpty(firstAgent.avatar_url, cfg.agent_avatar),
        welcome: firstNonEmpty(cfg.welcome_message, c.introMessage, defaultWelcome) || '',
    };
}
